import { addDays, addMonths, type LocalDate } from '../dates';
import { spendAmount } from '../ledger/counting';
import {
  cardCycle,
  cardDueDateIn,
  installmentCharges,
  previousStatement,
  type InstallmentCharge,
} from './installments';
import { addMonthsToMonth, dayInMonth, monthOf, occurrences } from './schedule';
import {
  cardMethod,
  dueId,
  installmentEntryId,
  type Due,
  type DueRowLike,
  type DueStatus,
  type InstallmentLike,
  type PayeeLike,
  type PaymentLike,
  type PaymentsSnapshot,
} from './types';

const live = <T extends { deletedAt: string | null }>(xs: readonly T[]) =>
  xs.filter((x) => x.deletedAt === null);

const sum = (xs: readonly number[]) => xs.reduce((s, x) => s + x, 0);

/** Card minimum (OPEN-QUESTIONS v0.5 Q4): 3% rounded up to the peso, at least ₱500, at most the statement. */
export function defaultMinimum(statementMinor: number): number {
  if (statementMinor <= 0) return 0;
  const pct = Math.ceil((statementMinor * 3) / 100 / 100) * 100;
  return Math.min(statementMinor, Math.max(50_000, pct));
}

/**
 * The day a payment's expense is dated (OPEN-QUESTIONS v0.5 Q1): a late payment counts on the due
 * date, so it lands in the month and cutoff it was due; an early or on-time one on the day paid.
 */
export function expenseDateFor(paidDate: LocalDate, dueDate: LocalDate | null): LocalDate {
  return dueDate !== null && paidDate > dueDate ? dueDate : paidDate;
}

interface Indexes {
  rows: Map<string, DueRowLike>;
  paymentsByDue: Map<string, PaymentLike[]>;
}

function index(snap: PaymentsSnapshot): Indexes {
  const rows = new Map(live(snap.dues).map((r) => [r.id, r]));
  const paymentsByDue = new Map<string, PaymentLike[]>();
  for (const p of live(snap.payments)) {
    if (!p.dueId) continue;
    const list = paymentsByDue.get(p.dueId) ?? [];
    list.push(p);
    paymentsByDue.set(p.dueId, list);
  }
  for (const list of paymentsByDue.values())
    list.sort((a, b) => (a.paidDate < b.paidDate ? -1 : a.paidDate > b.paidDate ? 1 : 0));
  return { rows, paymentsByDue };
}

function statusOf(
  amountMinor: number,
  paidMinor: number,
  stored: DueRowLike | null,
  prepaid: boolean,
): DueStatus {
  if (stored?.status === 'skipped') return 'skipped';
  if (stored?.status === 'closed') return 'closed';
  if (paidMinor > 0 && paidMinor >= amountMinor) return 'paid';
  if (prepaid) return 'prepaid';
  if (paidMinor > 0) return 'partial';
  return amountMinor <= 0 ? 'paid' : 'open';
}

function makeDue(
  snap: PaymentsSnapshot,
  idx: Indexes,
  payee: PayeeLike,
  period: LocalDate,
  amountMinor: number,
  extra: { estimated?: boolean; prepaid?: boolean; transfer?: boolean; card?: Due['card'] } = {},
): Due {
  const id = dueId(snap.userId, payee.id, period);
  const stored = idx.rows.get(id) ?? null;
  const payments = idx.paymentsByDue.get(id) ?? [];
  const paidMinor = sum(payments.map((p) => p.amountMinor));
  const prepaid = extra.prepaid === true && payments.length === 0;
  const status = statusOf(amountMinor, paidMinor, stored, prepaid);
  const open = status === 'open' || status === 'partial';
  return {
    id,
    payeeId: payee.id,
    payeeKind: payee.kind,
    payeeName: payee.name,
    period,
    dueDate: period,
    amountMinor,
    estimated: extra.estimated === true,
    paidMinor: prepaid ? amountMinor : paidMinor,
    remainingMinor: open ? Math.max(0, amountMinor - paidMinor) : 0,
    status,
    payments,
    stored,
    autoPay: payee.autoPay,
    transfer: extra.transfer === true,
    ...(extra.card ? { card: extra.card } : {}),
  };
}

/** Utility estimate (spec §5): the average of the last 3 actual amounts, else the starting estimate. */
export function utilityEstimate(
  payee: Pick<PayeeLike, 'amountMinor'>,
  earlierActuals: readonly number[],
): number {
  const last = earlierActuals.slice(-3);
  if (last.length === 0) return payee.amountMinor ?? 0;
  return Math.round(sum([...last]) / last.length);
}

/** Bills, utilities and fixed loans: one due per scheduled date. */
function scheduledDues(
  snap: PaymentsSnapshot,
  idx: Indexes,
  payee: PayeeLike,
  until: LocalDate,
): Due[] {
  const actuals: number[] = [];
  return occurrences(payee, until).map((period) => {
    const stored = idx.rows.get(dueId(snap.userId, payee.id, period)) ?? null;
    const prepaid = period < payee.trackFrom;
    if (payee.kind === 'utility') {
      const actual = stored?.amountMinor ?? null;
      const amount = actual ?? utilityEstimate(payee, actuals);
      if (actual !== null) actuals.push(actual);
      return makeDue(snap, idx, payee, period, amount, { estimated: actual === null, prepaid });
    }
    return makeDue(snap, idx, payee, period, stored?.amountMinor ?? payee.amountMinor ?? 0, {
      prepaid,
    });
  });
}

// ---------------------------------------------------------------------------------------------
// Cards

/** The first due month with a tracked statement: its due date is on or after trackFrom. */
function firstCardMonth(card: PayeeLike): string {
  // A cycle keyed by its statement month can be due a month or two later, so look back first.
  let m = addMonthsToMonth(monthOf(card.trackFrom), -3);
  for (let i = 0; i < 6 && cardDueDateIn(card, m) < card.trackFrom; i += 1)
    m = addMonthsToMonth(m, 1);
  return m;
}

const isInstallmentRow = (e: { sourceModule: string }) => e.sourceModule === 'payments';

/**
 * An installment's monthly charges, each at the amount its ledger row was written with when one
 * is live (a month charged before the installment was edited keeps what was charged), so card
 * statements, Budget and Spending agree.
 */
export function recordedCharges(
  snap: PaymentsSnapshot,
  i: InstallmentLike,
  card: PayeeLike,
): InstallmentCharge[] {
  const rows = new Map(
    snap.cardEntries
      .filter((e) => e.deletedAt === null && isInstallmentRow(e))
      .map((e) => [e.id, e.amountMinor]),
  );
  return installmentCharges(i, card).map((c) => {
    const recorded = rows.get(installmentEntryId(snap.userId, i.id, c.month));
    return recorded === undefined ? c : { ...c, amountMinor: recorded };
  });
}

/**
 * Card statements (spec §5) from the first tracked one up to `until`: card purchases between
 * statement dates, plus that month's installment charges, plus the unpaid rest of the previous
 * statement; the first one carries the opening balance. A statement closed before the card was
 * added is the opening balance alone. A typed real amount replaces the calculation.
 */
export function cardDues(
  snap: PaymentsSnapshot,
  card: PayeeLike,
  until: LocalDate,
  today: LocalDate,
  idx: Indexes = index(snap),
): Due[] {
  const out: Due[] = [];
  const method = cardMethod(card.id);
  const purchases = snap.cardEntries.filter(
    (e) => e.method === method && e.deletedAt === null && !isInstallmentRow(e),
  );
  const charges = live(snap.installments)
    .filter((i) => i.cardId === card.id)
    .flatMap((i) => recordedCharges(snap, i, card));
  let month = firstCardMonth(card);
  let carried = 0;
  for (let i = 0; i < 600; i += 1, month = addMonthsToMonth(month, 1)) {
    const { due, statement } = cardCycle(card, month);
    if (statement > until) break;
    const prevStatement = cardCycle(card, addMonthsToMonth(month, -1)).statement;
    const nextStatement = cardCycle(card, addMonthsToMonth(month, 1)).statement;
    const tracked = statement >= card.trackFrom;
    const openingMinor = i === 0 ? (card.openingBalanceMinor ?? 0) : 0;
    const purchasesMinor = tracked
      ? sum(
          purchases
            .filter((e) => e.date > prevStatement && e.date <= statement)
            .map((e) => spendAmount(e)),
        )
      : 0;
    const installmentsMinor = tracked
      ? sum(charges.filter((c) => c.date === due).map((c) => c.amountMinor))
      : 0;
    const id = dueId(snap.userId, card.id, due);
    const stored = idx.rows.get(id) ?? null;
    const computed = openingMinor + purchasesMinor + installmentsMinor + carried;
    const amount = stored?.amountMinor ?? computed;
    const minimumMinor = stored?.minimumMinor ?? defaultMinimum(amount);
    const d = makeDue(snap, idx, card, due, amount, {
      transfer: true,
      estimated: stored?.amountMinor == null && today <= statement && tracked,
      card: {
        statementDate: statement,
        minimumMinor,
        minimumEstimated: stored?.minimumMinor == null,
        carriedInMinor: carried,
        purchasesMinor,
        installmentsMinor,
        openingMinor,
      },
    });
    const rest = Math.max(0, amount - d.paidMinor);
    if (d.status === 'partial' || d.status === 'open') {
      if (today > nextStatement && rest > 0) {
        d.status = 'rolled';
        d.remainingMinor = 0;
      }
    }
    // The unpaid rest carries into the next statement only once this one has rolled (the next
    // statement is out); until then it's still owed here, never shown in both. A statement still
    // to come is assumed paid on time, so future statements show their own charges only.
    carried = d.status === 'rolled' ? rest : 0;
    out.push(d);
  }
  return out;
}

export interface CardSummary {
  balanceMinor: number;
  availableMinor: number | null;
  nextStatementDate: LocalDate;
  /** The statement to pay next (open, partial), or null. */
  nextDue: Due | null;
  statements: Due[];
}

/**
 * Current balance: opening balance + purchases since tracking began + installment months charged
 * so far on tracked statements - every statement payment.
 */
export function cardSummary(
  snap: PaymentsSnapshot,
  card: PayeeLike,
  today: LocalDate,
): CardSummary {
  const statements = cardDues(snap, card, addDays(today, 62), today);
  const first = statements[0];
  const trackStart = first
    ? first.card!.statementDate < card.trackFrom
      ? first.card!.statementDate
      : previousStatement(card, first.card!.statementDate)
    : card.trackFrom;
  const method = cardMethod(card.id);
  const purchases = sum(
    snap.cardEntries
      .filter(
        (e) =>
          e.method === method &&
          e.deletedAt === null &&
          !isInstallmentRow(e) &&
          e.date > trackStart &&
          e.date <= today,
      )
      .map((e) => spendAmount(e)),
  );
  const charges = sum(
    live(snap.installments)
      .filter((i) => i.cardId === card.id)
      .flatMap((i) => recordedCharges(snap, i, card))
      .filter((c) => c.tracked && c.date <= today)
      .map((c) => c.amountMinor),
  );
  const paid = sum(
    live(snap.payments)
      .filter((p) => p.payeeId === card.id)
      .map((p) => p.amountMinor),
  );
  const balanceMinor = (card.openingBalanceMinor ?? 0) + purchases + charges - paid;
  const upcoming = statements.find((s) => s.card!.statementDate >= today);
  return {
    balanceMinor,
    availableMinor:
      card.creditLimitMinor != null ? card.creditLimitMinor - Math.max(0, balanceMinor) : null,
    nextStatementDate: upcoming?.card!.statementDate ?? cardCycle(card, monthOf(today)).statement,
    nextDue:
      statements.find(
        (s) => (s.status === 'open' || s.status === 'partial') && s.amountMinor > 0,
      ) ?? null,
    statements,
  };
}

// ---------------------------------------------------------------------------------------------
// Loans

export interface BalancePoint {
  date: LocalDate;
  balanceMinor: number;
  projected: boolean;
}

export interface FlexibleSimulation {
  balanceMinor: number;
  interestMinor: number;
  paidMinor: number;
  points: BalancePoint[];
}

/** Interest on the balance left (spec: estimated, labeled so). 300 bp = 3% a month. */
function interestOn(balanceMinor: number, bp: number): number {
  return balanceMinor > 0 ? Math.round((balanceMinor * bp) / 10_000) : 0;
}

/**
 * The k-th month end strictly after the loan's start (k = 0: the end of the start month, or of
 * the next month when the loan starts on a month end, so no interest is added on day one).
 */
function monthEnd(startDate: LocalDate, k: number): LocalDate {
  const first = dayInMonth(monthOf(startDate), 31) > startDate ? 0 : 1;
  return dayInMonth(addMonthsToMonth(monthOf(startDate), k + first), 31);
}

/**
 * A flexible loan from its start to `until` (e.g. a family loan paid whenever): the balance on
 * startDate, interest added at the end of each month on whatever is left (compounding; a payment
 * on the last day counts before that day's interest), minus every payment. Never below zero.
 */
export function simulateFlexible(
  loan: Pick<PayeeLike, 'principalMinor' | 'interestBp' | 'startDate'>,
  payments: readonly Pick<PaymentLike, 'paidDate' | 'amountMinor'>[],
  until: LocalDate,
): FlexibleSimulation {
  let balance = loan.principalMinor ?? 0;
  let interest = 0;
  let paid = 0;
  const bp = loan.interestBp ?? 0;
  const points: BalancePoint[] = [
    { date: loan.startDate, balanceMinor: balance, projected: false },
  ];
  const sorted = [...payments]
    .filter((p) => p.paidDate <= until)
    .sort((a, b) => (a.paidDate < b.paidDate ? -1 : a.paidDate > b.paidDate ? 1 : 0));
  let k = 0;
  const postInterestThrough = (date: LocalDate, inclusive: boolean) => {
    for (let end = monthEnd(loan.startDate, k); inclusive ? end <= date : end < date;) {
      const add = interestOn(balance, bp);
      if (add > 0) {
        balance += add;
        interest += add;
        points.push({ date: end, balanceMinor: balance, projected: false });
      }
      k += 1;
      if (k > 1200) break;
      end = monthEnd(loan.startDate, k);
    }
  };
  for (const p of sorted) {
    postInterestThrough(p.paidDate, false);
    const applied = Math.min(balance, p.amountMinor);
    balance -= applied;
    paid += p.amountMinor;
    points.push({ date: p.paidDate, balanceMinor: balance, projected: false });
  }
  postInterestThrough(until, true);
  return { balanceMinor: balance, interestMinor: interest, paidMinor: paid, points };
}

/** Planned payments of a flexible loan from today: amounts (the last one smaller) until paid off. */
function projectFlexible(
  loan: PayeeLike,
  balanceToday: number,
  today: LocalDate,
  until: LocalDate,
): { date: LocalDate; amountMinor: number; balanceAfter: number }[] {
  const planned = loan.amountMinor ?? 0;
  if (loan.repeat === 'none' || planned <= 0 || balanceToday <= 0) return [];
  const bp = loan.interestBp ?? 0;
  const out: { date: LocalDate; amountMinor: number; balanceAfter: number }[] = [];
  let balance = balanceToday;
  let k = 0;
  while (monthEnd(loan.startDate, k) <= today && k < 1200) k += 1;
  const dates = occurrences(loan, until).filter((d) => d > today);
  for (const d of dates) {
    while (monthEnd(loan.startDate, k) < d && k < 1200) {
      balance += interestOn(balance, bp);
      k += 1;
    }
    const pay = Math.min(planned, balance);
    balance -= pay;
    out.push({ date: d, amountMinor: pay, balanceAfter: balance });
    if (balance <= 0) break;
  }
  return out;
}

function loanPayments(snap: PaymentsSnapshot, loanId: string): PaymentLike[] {
  return live(snap.payments).filter((p) => p.payeeId === loanId);
}

/** Flexible loans: planned dues from the day it was added; past ones stay open until paid or skipped. */
function flexibleDues(
  snap: PaymentsSnapshot,
  idx: Indexes,
  loan: PayeeLike,
  until: LocalDate,
  today: LocalDate,
): Due[] {
  const sim = simulateFlexible(loan, loanPayments(snap, loan.id), today);
  const projected = new Map(
    projectFlexible(loan, sim.balanceMinor, today, until).map((p) => [p.date, p.amountMinor]),
  );
  const planned = loan.amountMinor ?? 0;
  const out: Due[] = [];
  for (const period of occurrences(loan, until)) {
    if (period < loan.trackFrom) continue;
    const id = dueId(snap.userId, loan.id, period);
    const stored = idx.rows.get(id) ?? null;
    const acted = stored !== null || (idx.paymentsByDue.get(id)?.length ?? 0) > 0;
    let amount: number | undefined;
    if (acted) amount = stored?.amountMinor ?? planned;
    else if (period <= today)
      amount = sim.balanceMinor > 0 ? Math.min(planned, sim.balanceMinor) : 0;
    else amount = projected.get(period);
    if (amount === undefined || (!acted && amount <= 0)) continue;
    out.push(makeDue(snap, idx, loan, period, amount));
  }
  return out;
}

export interface LoanState {
  type: 'fixed' | 'flexible';
  balanceMinor: number;
  paidMinor: number;
  /** Fixed: every scheduled payment; flexible: borrowed + interest so far. */
  totalMinor: number | null;
  interestMinor: number;
  paymentsLeft: number | null;
  payoffDate: LocalDate | null;
  points: BalancePoint[];
}

/** Balance, payments left, payoff and the payoff curve (actual line, projected dots). */
export function loanState(snap: PaymentsSnapshot, loan: PayeeLike, today: LocalDate): LoanState {
  const idx = index(snap);
  if (loan.loanType === 'flexible') {
    const sim = simulateFlexible(loan, loanPayments(snap, loan.id), today);
    const proj = projectFlexible(loan, sim.balanceMinor, today, addMonths(today, 120));
    const done = proj.length > 0 && proj[proj.length - 1]!.balanceAfter <= 0;
    return {
      type: 'flexible',
      balanceMinor: sim.balanceMinor,
      paidMinor: sim.paidMinor,
      totalMinor: (loan.principalMinor ?? 0) + sim.interestMinor,
      interestMinor: sim.interestMinor,
      paymentsLeft: done ? proj.length : null,
      payoffDate: sim.balanceMinor <= 0 ? today : done ? proj[proj.length - 1]!.date : null,
      points: [
        ...sim.points,
        ...proj.map((p) => ({ date: p.date, balanceMinor: p.balanceAfter, projected: true })),
      ],
    };
  }
  const all = scheduledDues(snap, idx, loan, loan.endDate ?? '9999-12-31');
  const counted = all.filter((d) => d.status !== 'closed' && d.status !== 'skipped');
  const totalMinor = sum(counted.map((d) => d.amountMinor));
  const paidMinor = sum(counted.map((d) => Math.min(d.paidMinor, d.amountMinor)));
  const left = counted.filter((d) => d.status === 'open' || d.status === 'partial');
  let running = totalMinor;
  const points: BalancePoint[] = [];
  const firstDate = all[0]?.dueDate ?? loan.startDate;
  points.push({ date: addDays(firstDate, -1), balanceMinor: totalMinor, projected: false });
  for (const d of counted) {
    running -= d.amountMinor;
    points.push({
      date: d.dueDate,
      balanceMinor: Math.max(0, running),
      projected: d.status === 'open' || d.status === 'partial',
    });
  }
  return {
    type: 'fixed',
    balanceMinor: totalMinor - paidMinor,
    paidMinor,
    totalMinor,
    interestMinor: 0,
    paymentsLeft: left.length,
    payoffDate: counted[counted.length - 1]?.dueDate ?? null,
    points,
  };
}

// ---------------------------------------------------------------------------------------------
// All payees

/**
 * Dues the schedule no longer makes (the schedule was edited after paying): kept with what was
 * paid, so paid money never disappears from history or the cash flow.
 */
function orphanDues(
  snap: PaymentsSnapshot,
  idx: Indexes,
  payee: PayeeLike,
  have: Set<string>,
): Due[] {
  const out: Due[] = [];
  for (const [id, list] of idx.paymentsByDue) {
    if (have.has(id) || list[0]?.payeeId !== payee.id) continue;
    const period = list[0]?.period;
    if (!period) continue;
    const stored = idx.rows.get(id);
    const paid = sum(list.map((p) => p.amountMinor));
    out.push(makeDue(snap, idx, payee, period, stored?.amountMinor ?? paid));
  }
  return out;
}

/** Every due of one payee from its start up to `until`, oldest first. */
export function payeeDues(
  snap: PaymentsSnapshot,
  payee: PayeeLike,
  until: LocalDate,
  today: LocalDate,
  idx: Indexes = index(snap),
): Due[] {
  let dues: Due[];
  if (payee.kind === 'card') dues = cardDues(snap, payee, until, today, idx);
  else if (payee.kind === 'loan' && payee.loanType === 'flexible')
    dues = flexibleDues(snap, idx, payee, until, today);
  else dues = scheduledDues(snap, idx, payee, until);
  const have = new Set(dues.map((d) => d.id));
  return [...dues, ...orphanDues(snap, idx, payee, have)].sort(byDueDate);
}

export function byDueDate(a: Due, b: Due): number {
  return a.dueDate < b.dueDate
    ? -1
    : a.dueDate > b.dueDate
      ? 1
      : a.payeeName.localeCompare(b.payeeName);
}

/** Every live payee's dues up to `until`. */
export function allDues(snap: PaymentsSnapshot, until: LocalDate, today: LocalDate): Due[] {
  const idx = index(snap);
  return live(snap.payees)
    .flatMap((p) => payeeDues(snap, p, until, today, idx))
    .sort(byDueDate);
}

export const isOpen = (d: Pick<Due, 'status'>) => d.status === 'open' || d.status === 'partial';

/** Find one due by payee and period (the pay sheet's route). */
export function findDue(
  snap: PaymentsSnapshot,
  payeeId: string,
  period: LocalDate,
  today: LocalDate,
): Due | null {
  const payee = snap.payees.find((p) => p.id === payeeId && p.deletedAt === null);
  if (!payee) return null;
  const until = period > today ? addDays(period, 1) : addDays(today, 1);
  return payeeDues(snap, payee, until, today).find((d) => d.period === period) ?? null;
}
